import { Injectable, Injector } from "@angular/core";
import { FeatureName } from "@common/ADAPT.Common.Model/embed/feature-name.enum";
import { Organisation } from "@common/ADAPT.Common.Model/organisation/organisation";
import { IChatAiOrgProvider, IMegaPromptLabel } from "@common/lib/chat-ai/chat-ai-org-provider.interface";
import { ChatAiUiService } from "@common/lib/chat-ai/chat-ai-ui.service";
import { IMegaPromptEntry, MegaPromptMetadata } from "@common/lib/chat-ai/mega-prompts";
import { RouteService } from "@common/route/route.service";
import { RouteEventsService } from "@common/route/route-events.service";
import { debounceTime, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";
import { AuthorisationService } from "../authorisation/authorisation.service";
import { AuthorisationNotificationService } from "../authorisation/authorisation-notification.service";
import { FeaturesService } from "../features/features.service";
import { OrganisationService } from "../organisation/organisation.service";
import { CommonTeamsService } from "../teams/common-teams.service";

@Injectable({
    providedIn: "root",
})
export class ChatAiOrgService implements IChatAiOrgProvider {
    public readonly applicableMegaPromptLabels$: Observable<IMegaPromptLabel[]>;

    public constructor(
        private authNotificationService: AuthorisationNotificationService,
        private featuresService: FeaturesService,
        private authService: AuthorisationService,
        private teamsService: CommonTeamsService,
        private routeEventsService: RouteEventsService,
        private routeService: RouteService,
        private orgService: OrganisationService,
        injector: Injector,
    ) {
        this.applicableMegaPromptLabels$ = this.routeEventsService.navigationEnd$.pipe(
            startWith(undefined),
            debounceTime(500),
            switchMap(() => {
                const route = this.routeService.currentRoute;
                if (!route?.data?.megaPrompts?.length) {
                    return of([] as IMegaPromptLabel[]);
                }

                const routeTeamId = this.routeService.getRouteParam("teamId");
                const team$ = routeTeamId ? this.teamsService.getTeamById(Number(routeTeamId)) : of(undefined);
                const chatUiService = injector.get(ChatAiUiService);
                return chatUiService.getMegaPromptNames$().pipe(
                    switchMap((megaPromptNames) => team$.pipe(
                        map((team) => megaPromptNames
                            .filter((name) => {
                                const matchedEntry: IMegaPromptEntry | undefined = route.data!.megaPrompts.find(
                                    (entry: IMegaPromptEntry) => name === entry.name);
                                if (matchedEntry) {
                                    if (matchedEntry.condition && !matchedEntry.condition(team)) {
                                        return false;
                                    }

                                    const requiredPermissions = MegaPromptMetadata[matchedEntry.name].permissions;
                                    if (requiredPermissions.length > 0) {
                                        const teamEntity = routeTeamId ? { teamId: Number(routeTeamId) } : undefined;
                                        const hasPermission = requiredPermissions.some((p) => {
                                            const entity = p.globalOnly ? undefined : teamEntity;
                                            return this.authService.currentPersonHasOneOfPermissions([p.permissionName], entity);
                                        });
                                        if (!hasPermission) {
                                            return false; // user doesn't have permission for this prompt, so we won't show it as an option
                                        }
                                    }
                                }

                                return !!matchedEntry;
                            })
                            .map((promptName) => ({ promptName, label: MegaPromptMetadata[promptName].label })),
                        )),
                    ));
            }),
            shareReplay(1),
        );
    }

    public get authorisationChanged$() {
        return this.authNotificationService.authorisationChanged$;
    }

    public promiseToCheckIfFeatureActive(name: FeatureName) {
        return this.featuresService.promiseToCheckIfFeatureActive(name);
    }

    public getOrganisationId(): number {
        return this.orgService.getOrganisationId();
    }

    public get organisationSwitching$(): Observable<Organisation | undefined> {
        return this.orgService.organisationSwitching$;
    }

    public get organisationChanged$(): Observable<Organisation | undefined> {
        return this.orgService.organisationChanged$;
    }

    public notifyOrganisationChangedEvent(): void {
        this.orgService.notifyOrganisationChangedEvent();
    }
}
